/**
 * Formats numbers with commas for thousands separators
 * @param {number|string} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted number with commas
 */
export const formatNumberWithCommas = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  // Convert to number if it's a string
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  // Check if it's a valid number
  if (isNaN(numValue)) {
    return value.toString();
  }

  // Format with commas and specified decimal places
  return numValue.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Formats currency values with commas and dollar sign
 * @param {number|string} value - The amount to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted currency with commas
 */
export const formatCurrencyWithCommas = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') {
    return '$0.00';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return value.toString();
  }

  return numValue.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
};

/**
 * Formats numbers with commas only if they exceed 3 digits
 * @param {number|string} value - The number to format
 * @param {number} decimals - Number of decimal places (default: 2)
 * @returns {string} - Formatted number with commas if > 3 digits
 */
export const formatLargeNumbers = (value, decimals = 2) => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return value.toString();
  }

  // Check if the number has more than 3 digits (including decimals)
  const absValue = Math.abs(numValue);
  const integerPart = Math.floor(absValue);
  
  if (integerPart >= 1000) {
    return numValue.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }
  
  // For numbers less than 1000, just format with decimals
  return numValue.toFixed(decimals);
};
